import { createTheme } from '@mui/material';
import { fonts, colors, componentStyles } from '../styles/fontStyles';

const frostLightTheme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      smMd700: 700, // custom breakpoint
      smMd800: 800, // custom breakpoint
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
  palette: {
    mode: 'light',
    primary: {
      main: colors.primaryBlue,
    },
    background: {
      default: colors.gray150,
    },
  },
  buttons: {
    primaryBlue: {
      color: colors.white,
      borderColor: colors.primaryBlue,
      background: colors.primaryBlue,
      hover: {
        background: colors.blue800,
        borderColor: colors.blue700,
      },
      disabled: {
        background: colors.gray150,
        borderColor: 'transparent',
        color: colors.blue900,
      },
    },
    ghost: {
      color: colors.black,
      borderColor: colors.primaryBlue,
      hover: {
        color: colors.primaryBlue,
        borderColor: colors.primaryBlue,
      },
    },
  },
  typography: {
    fontFamily: fonts.regular,
    h1: {
      fontFamily: fonts.titlesNavButtons,
    },
    h2: {
      fontFamily: fonts.titlesNavButtons,
    },
    h3: {
      fontFamily: fonts.titlesNavButtons,
    },
    h4: {
      fontFamily: fonts.titlesNavButtons,
    },
    h5: {
      fontFamily: fonts.titlesNavButtons,
    },
    h6: {
      fontFamily: fonts.titlesNavButtons,
    },
    links: {
      default: {
        color: colors.primaryBlue,
        hoverColor: colors.primaryBlue,
      },
      desktopMenu: {
        color: colors.blue900,
        activeColor: colors.primaryBlue,
        underlineNavigationActive: colors.primaryBlue,
      },
    },
  },
  images: {
    logo: {
      src: '/frost/logo_blue.png',
    },
    activateKitIcon: {
      default: '/icons/qrcode-gray-light.svg',
      hover: '/icons/qrcode-blue-hover-light.png',
    },
    viewResultsIcon: {
      default: '/icons/description-blue900.svg',
      hover: '/icons/description-blue500.svg',
    },
    navigateBackIcon: {
      src: '/icons/back-blue-arrow.svg',
    },
    navigateNextIcon: {
      src: '/icons/next-blue-arrow.svg',
    },
    desktopMenu: {
      results: {
        default: '/icons/test_pipe_dark.svg',
        active: '/icons/test_pipe_blue_small.svg',
      },
      orders: {
        default: '/icons/shopping_cart_light_inactive.svg',
        active: '/icons/shopping_cart_light_active.svg',
      },
      support: {
        default: '/icons/support_help_light_inactive.svg',
        active: '/icons/support_help_light_active.svg',
      },
      messages: {
        default: '/icons/messages_gray550.svg',
        active: '/icons/messages_blue.svg',
      },
      calendar: {
        default: '/icons/calendar_gray550.svg',
        active: '/icons/calendar_gray550.svg',
      },
      records: {
        default: '/icons/medical_services_gray550.svg',
        active: '/icons/medical_services_gray550.svg',
      },
    },
    createAccount: {
      src: '/icons/register-blue.svg',
    },
    editIcon: {
      default: '/icons/pencil_blue900.svg',
      hover: '/icons/pencil_blue.svg',
    },
    deleteIcon: {
      default: '/icons/trash_can_blue900.svg',
      hover: '/icons/trash_can_blue500.svg',
    },
    closeIcon: {
      default: '/icons/close-blue900.svg',
      hover: '/icons/close-blue900.svg',
    },
    calendarIcon: {
      default: '/icons/calendar-blue900.svg',
      hover: '/icons/calendar-blue500.svg',
    },
    locationIcon: {
      default: '/icons/location-blue900.svg',
      hover: '/icons/location-blue500.svg',
    },
    peopleIcon: {
      default: '/icons/people-blue900.svg',
      hover: '/icons/people-blue500.svg',
    },
    requirementIcons: {
      lungs: {
        default: '/icons/lungs_default.svg',
        highlighted: '/icons/lungs_highlighted.svg',
      },
    },
    poweredByRH: {
      src: '/images/powered-by-RH-black-and-blue.png',
    },
  },
  custom: {
    // initially copied from darkTheme
    // once we update these ones we can remove this comment
    modalBorderRadius: componentStyles.modalBorderRadius,
    modalBackgroundColor: componentStyles.modalBackgroundColor,
    modalTextColor: colors.white,
    dividerColor: colors.white,
    buttonTextColor: colors.white,
    buttonTextColorVariantOne: colors.primaryBlue,
    buttonBackgroundColor: colors.primaryBlue,
    buttonBackgroundHoverColor: colors.darkPrimaryBlue,

    // new properties
    report: {
      dataDecoration: {
        not_detected: {
          backgroundColor: colors.green300,
          fillFontColor: colors.blue900,
        },
        inconclusive: {
          backgroundColor: colors.gray300,
          fillFontColor: colors.blue900,
        },
        below_range: {
          backgroundColor: colors.green300,
          fillFontColor: colors.blue900,
        },
        detected: {
          backgroundColor: colors.blue350,
          fillFontColor: colors.blue900,
        },
        above_range: {
          backgroundColor: colors.purple350,
          fillFontColor: colors.blue900,
        },
        unknown: {
          backgroundColor: colors.purple350,
          fillFontColor: colors.blue900,
        },
      },
      details: {
        header: {
          backgroundColor: colors.gray150,
          color: colors.blue900,
        },
        subHeader: {
          color: colors.gray550,
        },
        resultCard: {
          slider: {
            previousCardBorderColor: colors.gray550,
            currentCardBorderColor: colors.primaryBlue,
            nextCardBorderColor: colors.gray550,
          },
          backgroundColor: colors.white,
          dateChip: {
            backgroundColor: colors.gray150,
            color: colors.blue900,
          },
          compoundsDetected: {
            detectedCountTextColor: colors.primaryBlue,
            compoundDetectedTextColor: colors.gray550,
          },
          dividerLineColor: colors.gray550,
          totalPfas: {
            totalPfasScoreTextColor: colors.primaryBlue,
            pfasUnitColor: colors.gray550,
            totalPfasTextColor: colors.primaryBlue,
          },
        },
        reportNasemScoreBadge: {
          low: {
            exposureTextColor: colors.forestGreen,
            badgeTextColor: colors.forestGreen,
            badgeBackgroundColor: colors.lightGreen,
          },
          medium: {
            exposureTextColor: colors.grayBrown,
            badgeTextColor: colors.grayBrown,
            badgeBackgroundColor: colors.lightOrange,
          },
          high: {
            exposureTextColor: colors.redBrown,
            badgeTextColor: colors.redBrown,
            badgeBackgroundColor: colors.lightRed,
          },
        },
        resultTabNavigator: {
          button: {
            root: {
              color: colors.blue900,
              borderColor: colors.blue900,
              backgroundColor: 'transparent',
            },
            selected: {
              color: colors.white,
              borderColor: colors.blue900,
              backgroundColor: colors.primaryBlue,
              onHover: {
                backgroundColor: colors.primaryBlue,
              },
            },
          },
          tooltip: {
            color: colors.blue900,
            backgroundColor: colors.white,
            arrow: {
              color: colors.white,
            },
            boxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.15), 0px 1px 3px 0px rgba(0, 0, 0, 0.30)',
          },
          images: {
            comparison: {
              root: {
                src: '/icons/chart-bar-blue900.svg',
              },
              selected: {
                src: '/icons/chart_bar_white.svg',
              },
            },
            card: {
              root: {
                src: '/icons/card-blue900.svg',
              },
              selected: {
                src: '/icons/cards_white.svg',
              },
            },
            table: {
              root: {
                src: '/icons/list-blue900.svg',
              },
              selected: {
                src: '/icons/list_white.svg',
              },
            },
            bubble: {
              root: {
                src: '/icons/chart-bubble-blue900.svg',
              },
              selected: {
                src: '/icons/bubble-view-white.svg',
              },
            },
            timeline: {
              root: {
                src: '/icons/timeline-blue900.svg',
              },
              selected: {
                src: '/icons/timeline-white.svg',
              },
            },
          },
        },
        downloadPDFResult: {
          buttons: {
            ghost: {
              color: colors.primaryBlue,
            },
          },
          images: {
            download: {
              src: '/icons/download-blue500.svg',
            },
          },
        },
        infoSection: {
          disclaimer: {
            title: {
              color: colors.primaryBlue,
            },
            description: {
              color: colors.gray550,
            },
          },
          boxWrapper: {
            background: colors.transparent,
            fieldValue: {
              color: colors.blue900,
            },
            fieldName: {
              color: colors.primaryBlue,
            },
          },
        },
        compoundCardsList: {
          cardContainer: {
            background: colors.white,
            content: {
              compoundName: {
                color: colors.blue900,
              },
              unitDetails: {
                name: {
                  color: colors.gray550,
                },
                noValue: {
                  color: colors.gray550,
                },
                value: {
                  color: colors.primaryBlue,
                },
              },
            },
          },
          indicator: {
            detected: {
              value: colors.blue350,
            },
            not_detected: {
              value: '/icons/light-theme/light_circle_green_outlined.svg',
            },
            below_range: {
              value: '/icons/light-theme/light_circle_green_filled.svg',
            },
            above_range: {
              value: '/icons/light-theme/light_arrow_purple_up.svg',
            },
            inconclusive: {
              value: '/icons/light-theme/light_circle_gray_outlined.svg',
            },
          },
          compoundCardChipMeaning: {
            boxStyles: {
              color: colors.gray550,
            },
            chip: {
              default: {
                backgroundColor: colors.green300,
                color: colors.blue900,
              },
              belowRange: {
                color: colors.blue900,
              },
              detected: {
                backgroundColor: colors.blue350,
              },
              aboveRange: {
                backgroundColor: colors.purple200,
              },
            },
          },
          compoundDetailsResult: {
            backgroundColor: colors.white,
            renderBackToCompoundsListBtn: {
              default: {
                startIcon: '/icons/back-curved-arrow-blue900.svg',
                endIcon: '/icons/back-curved-arrow-blue900.svg',
              },
              hover: {
                startIcon: '/icons/back-curved-arrow-blue500.svg',
                endIcon: '/icons/back-curved-arrow-blue500.svg',
              },
              desktopBackButtonColor: colors.blue900,
              mobileBackButtonColor: colors.blue900,
            },
            renderCompoundReportDetails: {
              testNameAbbrColor: colors.blue900,
              testNameOnlyColor: colors.blue900,
              boxStyles: {
                color: colors.gray550,
                h3color: colors.blue900,
                h4Color: colors.blue900,
                pColor: colors.gray550,
              },
            },
          },
          compoundDetailsPagination: {
            default: {
              mobileBackIcon: '/icons/back-arrow-blue900.svg',
              mobileNextIcon: '/icons/next-arrow-blue900.svg',
              desktopBackIcon: '/icons/back-arrow-blue900.svg',
              desktopNextIcon: '/icons/next-arrow-blue900.svg',
            },
            hover: {
              mobileBackIcon: '/icons/back-blue-arrow.svg',
              mobileNextIcon: '/icons/next-blue-arrow.svg',
              desktopBackIcon: '/icons/back-blue-arrow.svg',
              desktopNextIcon: '/icons/next-blue-arrow.svg',
            },
            mobileNextButtonColor: colors.blue900,
            mobileBackButtonColor: colors.blue900,
            desktopNextButtonColor: colors.blue900,
            desktopBackButtonColor: colors.blue900,
          },
          compoundDetailsChart: {
            renderCardWithLabelChip: {
              backgroundColor: colors.transparent,
              color: colors.blue900,
              borderColor: colors.gray550,
              detectionFrequencyColor: colors.gray550,
            },
            renderLevelValue: {
              nonDisplayValueColor: colors.gray550,
              displayValueColor: colors.blue900,
              levelColor: colors.gray550,
              unitColor: colors.gray550,
            },
            renderBarChart: {
              barChart: {
                xAxisFillColor: colors.blue900,
                yAxisFillColor: colors.gray600,
                referenceLines: {
                  defaultStrokeColor: colors.gray550,
                  strokeColor: colors.gray600,
                  labelFillColor: colors.gray600,
                  y0: {
                    strokeColor: colors.gray550,
                    labelFillColor: colors.gray600,
                  },
                },
                xData: {
                  fillColor: colors.green300,
                },
                yData: {
                  fillColor: colors.blue350,
                },
                zData: {
                  specialLabelFillColor: colors.purple200,
                  fillColor: colors.blue350,
                },
                badgeLabelConcentrationColor: colors.blue900,
              },
            },
          },
        },
        resultTable: {
          backgroundColor: colors.white,
          borderRadius: '16px',
          boxShadow:
            '0px 145px 41px 0px rgba(30, 30, 30, 0.00), 0px 93px 37px 0px rgba(30, 30, 30, 0.01), 0px 52px 31px 0px rgba(30, 30, 30, 0.05)',
          styledTableRow: {
            backgroundColor: colors.white,
            onHover: {
              backgroundColor: colors.white,
            },
          },
          styledHeaderCell: {
            color: colors.blue900,
            borderBottomColor: colors.gray550,
            backgroundColor: colors.white,
          },
          styledLevelCell: {
            color: colors.blue900,
            borderBottomColor: colors.gray550,
          },
          styledRegularCell: {
            color: colors.blue900,
            borderBottomColor: colors.gray550,
          },
          paginationContainer: {
            backgroundColor: colors.white,
          },
          styledPagination: {
            paginationItemRoot: {
              backgroundColor: colors.blue900,
              color: colors.white,
            },
            onHover: {
              backgroundColor: colors.primaryBlue,
            },
            selected: {
              borderColor: colors.primaryBlue,
              color: colors.white,
              backgroundColor: colors.transparent,
            },
          },
          reportLabelTooltip: {
            tooltip: {
              backgroundColor: colors.white,
              color: colors.blue900,
              arrow: {
                color: colors.white,
              },
              boxShadowColor: '0px 4px 8px 0px rgba(0, 0, 0, 0.15), 0px 1px 3px 0px rgba(0, 0, 0, 0.30)',
            },
          },
          nameCell: {
            borderBottomColor: colors.gray550,
            shortName: {
              color: colors.blue900,
            },
            fullName: {
              color: colors.gray550,
            },
          },
          tableSort: {
            color: colors.blue900,
          },
          loadCompoundDetails: {
            backgroundColor: colors.gray100,
          },
          drawer: {
            backgroundColor: colors.white,
            closeIcon: '/icons/close-blue900.svg',
          },
        },
      },
      timeline: {
        container: {
          backgroundColor: colors.white,
        },
        comparisonSelector: {
          backgroundColor: colors.white,
          typography: {
            compareResults: {
              color: colors.blue900,
            },
            lowerThanYourResults: {
              color: colors.gray550,
            },
            populationBelowValue: {
              color: colors.gray550,
            },
            compoundFullname: {
              color: colors.blue900,
            },
            compoundNotInNhanes: {
              color: colors.blue900,
            },
            selectCompound: {
              color: colors.blue900,
            },
          },
        },
        dropdownWrapper: {
          root: {
            color: colors.gray550,
          },
          select: {
            iconImageSrc: '/icons/chevron_down_gray550.svg',
            notchedOutline: {
              default: { borderColor: colors.blue900 },
              hover: { borderColor: colors.blue900 },
              focused: {
                borderColor: colors.blue900,
                hover: {
                  borderColor: colors.blue900,
                },
              },
            },
            paper: {
              color: colors.gray550,
              backgroundColor: colors.white,
              borderColor: colors.blue900,
              selectedOption: {
                backgroundColor: colors.gray150,
              },
            },
          },
        },
        filterByGender: {
          iconImageSrc: '/icons/check_small_white.svg',
          label: {
            color: colors.blue900,
          },
          buttonSelected: {
            backgroundColor: colors.primaryBlue,
            borderColor: colors.primaryBlue,
            color: colors.white,
          },
          buttonSelectedHover: {
            backgroundColor: colors.blue800,
            borderColor: colors.blue700,
            color: colors.white,
          },
          buttonNotSelected: {
            backgroundColor: colors.white,
            borderColor: colors.gray550,
            color: colors.gray550,
          },
          buttonNotSelectedHover: {
            backgroundColor: colors.white,
            borderColor: colors.primaryBlue,
            color: colors.primaryBlue,
          },
        },
        nasemScoreTimelineChart: {
          customDoubleLabel: {
            left: {
              color: colors.gray600,
            },
            right: {
              color: colors.gray550,
            },
          },
          composedChart: {
            xAxis: {
              tick: {
                color: colors.gray550,
              },
              axisLine: {
                color: colors.gray600,
              },
              tickLine: {
                color: colors.gray600,
              },
            },
            yAxis: {
              tick: {
                color: colors.gray600,
              },
              axisLine: {
                color: colors.gray600,
              },
              tickLine: {
                color: colors.gray600,
              },
            },
            referenceLines: {
              stroke: {
                color: colors.gray600,
              },
              y0: {
                stroke: {
                  color: colors.gray600,
                },
                label: {
                  color: colors.gray600,
                },
              },
            },
            area: {
              low: {
                color: colors.lightMintGreen,
              },
              medium: {
                color: colors.lightBeige,
              },
              high: {
                color: colors.lightPink,
              },
            },
            line: {
              activeDot: {
                stroke: {
                  color: colors.black,
                },
              },
              dot: {
                stroke: {
                  color: colors.gray600,
                },
              },
            },
          },
          tooltip: {
            borderColor: colors.blue900,
            backgroundColor: colors.white,
            dateChip: {
              color: colors.blue900,
              backgroundColor: colors.gray150,
            },
            compoundsDetected: {
              detectedCountTextColor: colors.primaryBlue,
              compoundDetectedTextColor: colors.gray550,
            },
            dividerLineColor: colors.gray550,
            totalPfas: {
              totalPfasScoreTextColor: colors.primaryBlue,
              pfasUnitColor: colors.gray550,
              totalPfasTextColor: colors.primaryBlue,
            },
          },
        },
        compoundInNHANESTimelineChart: {
          customDoubleLabel: {
            left: {
              color: colors.gray600,
            },
            right: {
              color: colors.gray550,
            },
          },
          composedChart: {
            xAxis: {
              tick: {
                color: colors.gray550,
              },
              axisLine: {
                color: colors.gray600,
              },
              tickLine: {
                color: colors.gray600,
              },
            },
            yAxis: {
              tick: {
                color: colors.gray600,
              },
              axisLine: {
                color: colors.gray600,
              },
              tickLine: {
                color: colors.gray600,
              },
            },
            referenceLines: {
              stroke: {
                color: colors.gray600,
              },
              y0: {
                stroke: {
                  color: colors.gray600,
                },
                label: {
                  color: colors.gray600,
                },
              },
            },
            line: {
              activeDot: {
                default: {
                  circle: {
                    stroke: {
                      color: colors.blue900,
                    },
                  },
                },
              },
              dot: {
                isBeyondReportableRange: {
                  line: {
                    stroke: {
                      color: colors.blue350,
                    },
                  },
                },
                isAboveRange: {
                  line: {
                    stroke: {
                      color: colors.purple350,
                    },
                  },
                },
                default: {
                  line: {
                    stroke: {
                      color: colors.blue350,
                    },
                  },
                  circle: {
                    fill: {
                      color: colors.blue350,
                    },
                    stroke: {
                      color: colors.blue350,
                    },
                  },
                },
              },
            },
            images: {
              inconclusive: '/icons/circle-outlined-gray550.svg',
              notDetected: '/icons/circle-outlined-green350.svg',
              aboveRange: '/icons/arrow-up-purple350.svg',
              belowRange: '/icons/circle-filled-green350.svg',
              beyondReportableRange: '/icons/arrow-up-blue350.svg',
            },
          },
          tooltip: {
            borderColor: colors.blue900,
            backgroundColor: colors.white,
            compoundDisplayValue: {
              color: colors.primaryBlue,
            },
            compoundLevel: {
              color: colors.gray550,
            },
            dividerLineColor: colors.gray550,
            compoundDetectionFrequency: {
              color: colors.gray550,
            },
            compoundLoq: {
              color: colors.primaryBlue,
            },
            compoundAboveRange: {
              color: colors.gray550,
            },
          },
        },
        compoundNotInNHANESTimelineChart: {
          customDoubleLabel: {
            left: {
              color: colors.gray600,
            },
            right: {
              color: colors.gray550,
            },
          },
          composedChart: {
            xAxis: {
              tick: {
                color: colors.gray550,
              },
              axisLine: {
                color: colors.gray600,
              },
              tickLine: {
                color: colors.gray600,
              },
            },
            yAxis: {
              tick: {
                color: colors.gray600,
              },
              axisLine: {
                color: colors.gray600,
              },
              tickLine: {
                color: colors.gray600,
              },
            },
            referenceLines: {
              stroke: {
                color: colors.gray600,
              },
            },
            line: {
              activeDot: {
                default: {
                  circle: {
                    stroke: {
                      color: colors.blue900,
                    },
                  },
                },
              },
              dot: {
                isAboveRange: {
                  line: {
                    stroke: {
                      color: colors.purple350,
                    },
                  },
                },
                default: {
                  line: {
                    stroke: {
                      color: colors.blue350,
                    },
                  },
                  circle: {
                    fill: {
                      color: colors.blue350,
                    },
                    stroke: {
                      color: colors.blue350,
                    },
                  },
                },
              },
            },
            images: {
              inconclusive: '/icons/circle-outlined-gray550.svg',
              notDetected: '/icons/circle-outlined-green350.svg',
              aboveRange: '/icons/arrow-up-purple350.svg',
              belowRange: '/icons/circle-filled-green350.svg',
              beyondReportableRange: '/icons/arrow-up-blue350.svg',
            },
          },
        },
      },
      yourNasemScaleWithGuidelines: {
        backgroundColor: colors.white,
        nasemLevelColors: {
          low: colors.forestGreen,
          medium: colors.grayBrown,
          high: colors.redBrown,
        },
        renderNasemGuidelines: {
          guidelines: {
            default: {
              color: colors.blue900,
              low: {
                backgroundColor: colors.lightMintGreen,
              },
              medium: {
                backgroundColor: colors.lightBeige,
              },
              high: {
                backgroundColor: colors.lightPink,
              },
            },
            selected: {
              low: {
                backgroundColor: colors.lightGreen,
              },
              medium: {
                backgroundColor: colors.lightOrange,
              },
              high: {
                backgroundColor: colors.lightRed,
              },
            },
          },
          yourCurrentPFASValueColor: colors.blue900,
        },
        renderYourNasemScale: {
          renderCollapseBtn: {
            backgroundColor: colors.gray150,
            collapseIcon: '/icons/chevron-down-blue900.svg',
          },
          nasemTextSubTitle: {
            color: colors.blue900,
          },
          nasemGuidance: {
            color: colors.blue900,
          },
        },
      },
      pfasAdviceGuidance: {
        box: {
          backgroundColor: colors.gray150,
        },
        pfasLowNasemLevelAdvice: {
          color: colors.blue900,
          understandYourTest: {
            defaultIcon: '/icons/description-blue900.svg',
            hoverIcon: '/icons/description-blue500.svg',
          },
        },
        pfasMediumNasemLevelAdvice: {
          renderScreeningAdvice: {
            color: colors.blue900,
            linkColor: colors.primaryBlue,
          },
          renderHypertensiveAdvice: {
            color: colors.blue900,
            linkColor: colors.primaryBlue,
          },
          renderBreastCancerAdvice: {
            color: colors.blue900,
            linkColor: colors.primaryBlue,
          },
        },
        pfasHighNasemLevelAdvice: {
          color: colors.blue900,
          atAllWellVisitColor: colors.redBrown,
          linkColor: colors.primaryBlue,
        },
      },
      comparison: {
        container: {
          backgroundColor: colors.white,
        },
        renderCustomXAxisTick: {
          color: colors.gray550,
          specialTick: {
            twoColor: colors.forestGreen,
            twentyColor: colors.redBrown,
          },
        },
        composedChart: {
          gridColor: colors.gray600,
          xAxis: {
            axisLine: {
              stroke: colors.gray600,
            },
          },
          yAxis: {
            tick: {
              fill: colors.gray600,
            },
            axisLine: {
              stroke: colors.gray600,
            },
            tickLine: {
              stroke: colors.gray600,
            },
          },
          area: {
            low: {
              fill: colors.lightMintGreen,
            },
            medium: {
              fill: colors.lightBeige,
            },
            high: {
              fill: colors.lightPink,
            },
          },
          line: {
            stroke: colors.primaryBlue,
          },
        },
        reportNasemScoreBadgeAndText: {
          backgroundColor: colors.white,
          dividerLineColor: colors.gray550,
          yourNasemScoreColor: colors.gray550,
          exposureColor: {
            low: {
              color: colors.forestGreen,
            },
            medium: {
              color: colors.grayBrown,
            },
            high: {
              color: colors.redBrown,
            },
          },
        },
        barChart: {
          customDoubleLabel: {
            leftFill: colors.gray600,
            rightFill: colors.gray550,
          },
          referenceLines: {
            stroke: colors.gray600,
            y0: {
              stroke: colors.gray600,
              label: {
                fill: colors.gray600,
              },
            },
          },
          bar: {
            low: {
              fill: colors.lightMintGreen,
            },
            medium: {
              fill: colors.lightBeige,
            },
            high: {
              fill: colors.lightPink,
            },
          },
        },
      },
      resultCTAOptions: {
        primaryBlueButton: {
          imageIcon: '/icons/download_white.svg',
        },
        understandYourTest: {
          defaultIcon: '/icons/description-blue900.svg',
          hoverIcon: '/icons/description-blue500.svg',
        },
        viewYourLatestResults: {
          defaultIcon: '/icons/description-blue900.svg',
          hoverIcon: '/icons/description-blue500.svg',
        },
      },
      howToUnderstandPfasCorePanelResult: {
        typography: {
          color: colors.blue900,
          h4: {
            color: colors.gray550,
          },
        },
        renderExplainer: {
          color: colors.gray550,
        },
        renderTabsTitle: {
          root: {
            color: colors.gray700,
          },
          selected: {
            color: colors.blue700,
          },
          backgroundColor: colors.white,
        },
      },
      bubbleViewContent: {
        backgroundColor: colors.white,
        typography: {
          body1: {
            color: colors.blue900,
          },
          body2: {
            color: colors.blue900,
          },
        },
        bubbleChartChipMeaning: {
          chipStyles: {
            backgroundColor: colors.green300,
            color: colors.blue900,
          },
          box: {
            color: colors.gray700,
          },
          chip: {
            notDetected: {
              color: colors.blue900,
            },
            belowRange: {
              color: colors.blue900,
            },
            detected: {
              backgroundColor: colors.blue350,
            },
            inconclusive: {
              borderColor: colors.gray550,
              backgroundColor: colors.gray150,
              color: colors.blue900,
            },
            aboveRange: {
              backgroundColor: colors.purple350,
            },
          },
        },
        compoundLevelsBubbleChart: {
          notDetectedIcon: '/icons/circle-outlined-green350.svg',
          belowRangeIcon: '/icons/circle-filled-green350.svg',
          aboveRangeIcon: '/icons/arrow-up-purple350.svg',
          inconclusiveIcon: '/icons/circle-outlined-gray550.svg',
          customScatterPoint: {
            detected: {
              circle: {
                fillColor: colors.blue350,
              },
            },
          },
          customTooltip: {
            backgroundColor: colors.white,
            color: colors.blue900,
            typography: {
              subtitle2: {
                color: colors.blue900,
              },
              body2: {
                color: colors.blue900,
              },
            },
          },
          composedChart: {
            xAxis: {
              tick: {
                strokeColor: colors.gray550,
              },
              axisLine: {
                strokeColor: colors.gray550,
              },
            },
            line: {
              strokeColor: colors.gray150,
            },
          },
        },
      },
    },
    pages: {
      guest: {
        home: {
          backgroundColor: colors.white,
          typography: {
            color: colors.blue900,
          },
          featureCards: {
            backgroundColor: 'rgba(0, 84, 252, 0.04)',
          },
          disclosure: {
            backgroundColor: 'rgba(0, 84, 252, 0.08)',
            borderColor: colors.primaryBlue,
          },
          services: {
            dividerColor: 'rgba(17, 32, 58, 0.14)',
            card: {
              backgroundColor: colors.white,
              borderColor: 'rgba(0, 84, 252, 0.12)',
              boxShadow: '0px 8px 24px rgba(17, 32, 58, 0.06)',
            },
            items: {
              hearingConservation: {
                accentColor: colors.frostOrange,
                surfaceColor: colors.frostOrangeSoft,
              },
              occupationalServices: {
                accentColor: colors.darkPrimaryBlue,
                surfaceColor: colors.darkPrimaryBlueSoft,
              },
              respiratoryProtection: {
                accentColor: colors.respiratoryBlue,
                surfaceColor: colors.respiratoryBlueSoft,
              },
              emergencyPersonnelServices: {
                accentColor: colors.red500,
                surfaceColor: colors.red500Soft,
              },
              hospitalServices: {
                accentColor: colors.greenDark,
                surfaceColor: colors.greenDarkSoft,
              },
              agriculturalServices: {
                accentColor: colors.agricultureGreen,
                surfaceColor: colors.agricultureGreenSoft,
              },
              schoolVisionHearingScreening: {
                accentColor: colors.blue600,
                surfaceColor: colors.blue600Soft,
              },
            },
          },
        },
      },
      footer: {
        copyright: {
          color: colors.gray550,
        },
        termsOfService: {
          color: colors.gray550,
        },
        privacyPolicy: {
          color: colors.gray550,
        },
        support: {
          color: colors.gray550,
          images: {
            src: '/icons/help-gray.svg',
          },
        },
      },
      userLogin: {
        backgroundColor: colors.white,
        typography: {
          h4: {
            color: colors.blue900,
          },
          subtitle1: {
            color: colors.blue900,
          },
          links: {
            color: colors.primaryBlue,
          },
        },
        textField: {
          label: {
            color: colors.blue900,
          },
        },
      },
      auth: {
        home: {
          backgroundColor: colors.white,
          typography: {
            color: colors.blue900,
            subHeader: {
              color: colors.blue900,
            },
          },
          desktopMenu: {
            avatar: {
              backgroundColor: colors.purple300,
              borderColor: colors.white,
              color: colors.white,
            },
            notificationCount: {
              badge: {
                backgroundColor: colors.primaryBlue,
                borderColor: colors.white,
                color: colors.white,
              },
            },
          },
        },
      },
      howTo: {
        whatYourResultsMean: {
          backgroundColor: colors.white,
          renderSummaryStatistics: {
            typography: {
              body1: {
                color: colors.primaryBlue,
              },
              body2: {
                color: colors.blue900,
              },
            },
          },
          summaryStatistics: {
            boxStyles: {
              backgroundColor: colors.gray150,
            },
            typography: {
              body1: {
                color: colors.blue900,
              },
              body2: {
                color: colors.blue900,
              },
            },
          },
          renderAboutTestAndMedicalEffects: {
            boxStyles: {
              backgroundColor: colors.gray150,
            },
            typography: {
              body1: {
                color: colors.blue900,
              },
              body2: {
                color: colors.blue900,
              },
              link: {
                color: colors.primaryBlue,
              },
            },
          },
          renderResultTableDetails: {
            typography: {
              body1: {
                color: colors.blue900,
              },
              body2: {
                color: colors.gray550,
              },
              link: {
                color: colors.primaryBlue,
              },
            },
          },
          renderSingleTableHeaderAndRow: {
            styledTableContainer: {
              backgroundColor: colors.gray150,
            },
            styledTableRow: {
              backgroundColor: colors.gray150,
              hover: {
                backgroundColor: colors.gray150,
              },
            },
            styledHeaderCell: {
              color: colors.blue900,
              borderBottom: colors.gray550,
              backgroundColor: colors.gray150,
            },
            styledShortNameCell: {
              color: colors.blue900,
              borderBottom: colors.gray150,
            },
            styledRegularCell: {
              color: colors.gray550,
              borderBottom: colors.gray150,
            },
            headerSubtextStyle: {
              color: colors.blue900,
            },
            styledName: {
              color: colors.gray550,
            },
            nhanesIcon: '/icons/arrows-sort-blue900.svg',
            chip: {
              backgroundColor: colors.blue350,
              color: colors.blue900,
            },
          },
        },
        howToUnderstandPFASStyles: {
          primaryBulletStyle: {
            color: colors.blue900,
          },
          primaryListItemStyle: {
            color: colors.blue900,
          },
          secondaryListItemStyle: {
            color: colors.gray550,
          },
          secondaryBulletStyle: {
            color: colors.gray550,
          },
        },
        howToReducePfas: {
          backgroundColor: colors.white,
          renderImmediateIdeasToTry: {
            immediateIdeas: {
              eatLessIcon: '/icons/pizza-off-gray550.svg',
              noPlasticIcon: '/icons/soup-gray550.svg',
              noSingleUseCup: '/icons/mug-gray550.svg',
              getWaterTestIcon: '/icons/droplet-gray550.svg',
              avoidNonStickIcon: '/icons/grill-spatula-gray550.svg',
              avoidStrainResistantFabrics: '/icons/jacket-gray550.svg',
              pfasFreeCosmeticsIcon: '/icons/perfume-gray550.svg',
            },
            ideaCard: {
              color: colors.blue900,
              backgroundColor: colors.gray150,
            },
            typography: {
              body1: {
                color: colors.primaryBlue,
              },
              body2: {
                color: colors.blue900,
              },
            },
          },
          renderWorkplacePFASExposure: {
            typography: {
              body1: {
                color: colors.blue900,
              },
              body2: {
                color: colors.gray550,
              },
            },
            renderWorkplacePFASInfo: {
              color: colors.blue900,
              boxStyles: {
                backgroundColor: colors.gray150,
              },
            },
            renderImportanceOfPFASMonitoring: {
              typography: {
                body1: {
                  color: colors.blue900,
                },
                body2: {
                  color: colors.gray550,
                },
              },
            },
          },
        },
        aboutTheTestMethodology: {
          backgroundColor: colors.white,
          renderUnderstandingTerminology: {
            typography: {
              body1: {
                color: colors.primaryBlue,
              },
              body2: {
                color: colors.blue900,
              },
            },
            infoCard: {
              boxStyles: {
                backgroundColor: colors.gray150,
              },
              descriptionTextStyle: {
                color: colors.blue900,
              },
              headerTextStyle: {
                color: colors.blue900,
              },
            },
          },
          renderUsefulReferences: {
            typography: {
              body1: {
                color: colors.blue900,
              },
            },
            list: {
              color: colors.gray550,
            },
            styledLink: {
              color: colors.primaryBlue,
            },
          },
        },
        takeSampleCollection: {
          tabs: {
            titleHeader: {
              selected: {
                color: colors.primaryBlue,
              },
              inactive: {
                color: colors.gray550,
              },
            },
            collectingSampleWarning: {
              color: colors.gray550,
            },
          },
          boxContainer: {
            background: colors.white,
            borderRadius: '16px',
            padding: '17px 31px 15px 31px',
          },
          allocateSomeMinutes: {
            backgroundColor: colors.gray150,
            color: colors.blue900,
          },
        },
      },
      frost: {
        clientDownloadEmailTemplatePage: {
          typography: {
            h2: {
              color: colors.black,
            },
            h6: {
              color: colors.primaryBlue,
            },
          },
        },
        authHome: {
          typography: {
            body1: {
              color: colors.blue900,
            },
            body2: {
              color: colors.gray700,
            },
          },
          cards: {
            event: {
              backgroundColor: colors.gray150,
            },
            borderColor: colors.gray250,
            backgroundColor: colors.white,
          },
        },
        records: {
          header: {
            title: colors.blue900,
            subtitle: colors.gray550,
            buttons: {
              filter: {
                color: colors.gray700,
                borderColor: colors.gray300,
                background: colors.white,
                hoverBackground: colors.gray150,
              },
              download: {
                background: colors.primaryBlue,
                hoverBackground: colors.blue700,
              },
            },
          },
          statusCards: {
            workStatus: {
              title: colors.gray550,
              compliantColor: colors.blue900,
              iconBackground: 'rgba(48, 209, 88, 0.1)',
              iconColor: colors.greenDark,
              borderColor: colors.gray150,
            },
            actionRequired: {
              title: colors.gray550,
              barColor: colors.orangeDark,
              card1: {
                background: colors.lightBeige,
                borderColor: colors.lightOrange,
                iconColor: colors.warningDark,
                textColor: colors.blue900,
                buttonBackground: colors.white,
                buttonText: colors.warningDark,
              },
              card2: {
                background: colors.gray150,
                borderColor: colors.gray100,
                iconColor: colors.slateGray,
                textColor: colors.blue900,
                scheduleText: colors.primaryBlue,
              },
            },
          },
          documentCards: {
            title: colors.blue900,
            blue: {
              background: 'rgba(92, 144, 247, 0.1)',
              text: colors.primaryBlue,
              hover: colors.blue700,
            },
            purple: {
              background: 'rgba(198, 48, 233, 0.1)',
              text: colors.purple600,
              hover: colors.purple700,
            },
            teal: {
              background: 'rgba(3, 218, 178, 0.1)',
              text: colors.greenTurquoise,
              hover: colors.forestGreen,
            },
            indigo: {
              background: 'rgba(1, 27, 78, 0.1)',
              text: colors.midnightBlue,
              hover: colors.darkNavy,
            },
            cardBorder: colors.gray150,
            cardHoverShadow: '0px 4px 6px -1px rgba(0, 0, 0, 0.1), 0px 2px 4px -1px rgba(0, 0, 0, 0.06)',
            tagBackground: colors.gray150,
            tagText: colors.gray600,
          },
          recentActivity: {
            title: colors.blue900,
            headerBackground: colors.gray150,
            headerText: colors.gray550,
            rowHover: colors.gray150,
            rowText: colors.blue900,
            actionIcon: colors.slateGray,
            actionIconHover: colors.primaryBlue,
            badges: {
              result: {
                background: 'rgba(92, 144, 247, 0.15)',
                color: colors.primaryBlue,
              },
              visit: {
                background: 'rgba(198, 48, 233, 0.1)',
                color: colors.purple600,
              },
              form: {
                background: 'rgba(3, 218, 178, 0.1)',
                color: colors.greenTurquoise,
              },
            },
            status: {
              normal: colors.greenDark,
              completed: colors.gray550,
            },
          },
        },
      },
    },
    components: {
      toolTipFromDB: {
        images: {
          src: '/icons/tooltip-gray.svg',
        },
        tooltip: {
          backgroundColor: colors.white,
          color: colors.blue900,
          arrow: {
            color: colors.white,
          },
          boxShadowColor: '0px 4px 8px 0px rgba(0, 0, 0, 0.15), 0px 1px 3px 0px rgba(0, 0, 0, 0.30)',
        },
      },
      headerBanner: {
        background: colors.white,
        title: {
          color: colors.blue900,
        },
        subtitle: {
          color: colors.primaryBlue,
        },
        description: {
          color: colors.blue900,
        },
      },
      textWithPrimaryBlueBackground: {
        background: colors.primaryBlue,
        color: colors.white,
      },
      mainUserRegistration: {
        backgroundColor: colors.white,
      },
      registrationStepper: {
        backgroundColor: colors.white,
      },
      customStepper: {
        boxStyles: {
          backgroundColor: colors.white,
        },
        backgroundColor: colors.gray150,
        progressBar: {
          backgroundColor: colors.primaryBlue,
        },
      },
      signupStep1AccountDetails: {
        typography: {
          color: colors.blue650,
          secondary: {
            color: colors.gray800,
          },
        },
      },
      signupStep2ProfileInfo: {
        typography: {
          color: colors.frostOrange,
        },
      },
      signupStep3Confirmation: {
        typography: {
          color: colors.blue650,
        },
      },
      signupStep4Complete: {
        typography: {
          color: colors.blue650,
        },
      },
      textField: {
        color: colors.blue900,
        label: {
          color: colors.blue900,
        },
        muiOutlinedInputRoot: {
          color: colors.gray550,
        },
      },
      warningToMailSampleBack: {
        exclamationIcon: {
          src: '/icons/light-theme/exclamation-black.svg',
        },
        title: {
          color: colors.blue900,
        },
      },
      commonTestKitActivation: {
        over18Alert: {
          container: {
            backgroundColor: colors.gray150,
            typography: {
              firstMessage: {
                color: colors.blue900,
              },
              link: {
                color: colors.gray550,
              },
            },
          },
        },
      },
      guestTestKitActivation: {
        containerStyles: {
          background: colors.white,
          borderRadius: '16px',
          marginTop: '22px',
          paddingTop: '1px',
        },
        headerStepper: {
          backOption: {
            color: colors.blue900,
          },
          image: {
            src: '/icons/light-theme/exclamation-black.svg',
          },
          title: {
            color: colors.blue900,
          },
        },
        steps: {
          successfulActivationOrRegistration: {
            explanationText: {
              color: colors.blue900,
            },
          },
        },
      },
      authTestKitActivation: {
        containerStyles: {
          background: colors.white,
          borderRadius: '16px',
          padding: '17px 31px 0px 31px',
        },
      },
      customDialog: {
        content: {
          backgroundColor: colors.white,
          color: colors.blue800,
          title: {
            color: colors.blue800,
          },
          closeIcon: {
            src: '/icons/close-blue900.svg',
          },
        },
      },
      forgotPassword: {
        backgroundColor: colors.white,
        typography: {
          color: colors.blue900,
        },
      },
      resetPasswordConfirmation: {
        backgroundColor: colors.white,
        typography: {
          color: colors.blue900,
        },
      },
      customDatePicker: {
        popperContainer: {
          borderColor: colors.blue900,
          backgroundColor: colors.white,
        },
        slotProps: {
          optionButtonStyles: {
            hover: {
              color: colors.gray900,
              backgroundColor: colors.gray300,
            },
            selected: {
              backgroundColor: colors.primaryBlue,
              color: colors.white,
              hover: {
                color: colors.gray900,
                backgroundColor: colors.gray300,
              },
              focus: {
                backgroundColor: colors.primaryBlue,
              },
            },
          },
        },
      },
      userHipaaNoticeAck: {
        typography: {
          color: colors.blue650,
          body1: {
            color: colors.blue900,
          },
        },
      },
    },
    profile: {
      editProfile: {
        editProfileButton: {
          color: colors.primaryBlue,
          imgSrc: '/icons/pencil_blue.svg',
        },
      },
    },
    drawerMobileMenu: {
      background: colors.gray150,
      listItemTextStyle: {
        color: colors.blue900,
      },
      notificationCountStyle: {
        color: colors.primaryBlue,
      },
      avatar: {
        fullName: {
          color: colors.blue900,
        },
        email: {
          color: colors.gray550,
        },
      },
      icons: {
        profile: '/icons/user_blue.svg',
        messages: '/icons/messages_blue.svg',
        order: '/icons/shopping_cart_blue.svg',
        activate: '/icons/qrcode-blue.svg',
        results: '/icons/test_pipe_blue_small.svg',
        orders: '/icons/view-orders.svg',
        login: '/icons/user_blue.svg',
        signup: '/icons/register-hand-button.png',
        support: '/icons/help-blue.svg',
        privacyPolicy: '/icons/privacy-policy_blue.svg',
        termsOfService: '/icons/terms-of-service_blue.svg',
        logout: '/icons/logout_blue.svg',
      },
      iconButton: {
        color: colors.blue900,
      },
    },
    messages: {
      container: {
        background: colors.white,
      },
      modalStyle: {
        backgroundColor: colors.gray150,
        color: colors.gray550,
      },
      showUnread: {
        checkBoxColor: colors.blue900,
        checkboxCheckedColor: colors.primaryBlue,
        checkboxBorder: colors.blue900,
        labelColor: colors.blue900,
      },
      list: {
        backgroundColor: colors.gray150,
        labelColor: colors.blue900,
        subjectColor: colors.gray550,
        createdAtColor: colors.gray550,
        borderUnread: `1px solid ${colors.primaryBlue}`,
      },
      modal: {
        labelColor: colors.blue900,
        createdAtColor: colors.gray550,
        dividerColor: colors.gray100,
        descriptionColor: colors.blue900,
        closeButtonColor: colors.gray550,
        linkColor: colors.primaryBlue,
      },
    },
    snackBarAlert: {
      severityTypeStyles: {
        success: {
          backgroundColor: colors.lightGreen,
          color: colors.forestGreen,
        },
        info: {
          backgroundColor: colors.primaryBlue,
          color: colors.white,
        },
        warning: {
          backgroundColor: colors.lightOrange,
          color: colors.grayBrown,
        },
        error: {
          backgroundColor: colors.lightRed,
          color: colors.redBrown,
        },
      },
    },
    orderPFAS: {
      container: {
        backgroundColor: colors.white,
      },
      stepper: {
        backgroundColor: colors.white,
        backLinkColor: colors.blue900,
        progressBar: {
          backgroundColor: colors.primaryBlue,
        },
      },
      first: {
        shippingTextColor: colors.primaryBlue,
      },
      subjects: {
        adultsOnlyMessage: {
          background: colors.gray150,
          checkbox: {
            color: colors.blue900,
            colorChecked: colors.blue900,
          },
          color: colors.blue900,
          colorTos: colors.gray550,
          colorTosLink: colors.gray550,
        },
      },
      subheader: {
        background: colors.primaryBlue,
        color: colors.white,
      },
      checkout: {
        redirectInstructionsColor: colors.primaryBlue,
        buttonInstructionsColor: colors.gray550,
      },
      thankyou: {
        color: colors.blue900,
      },
    },
  },
  support: {
    backgroundColor: colors.white,
    description: {
      color: colors.blue900,
    },
    categoryButton: {
      buttonSelected: {
        backgroundColor: colors.primaryBlue,
        borderColor: colors.primaryBlue,
        color: colors.white,
      },
      buttonSelectedHover: {
        backgroundColor: colors.blue800,
        borderColor: colors.blue700,
        color: colors.white,
      },
      buttonNotSelected: {
        backgroundColor: colors.white,
        borderColor: colors.gray550,
        color: colors.gray550,
      },
      buttonNotSelectedHover: {
        backgroundColor: colors.white,
        borderColor: colors.primaryBlue,
        color: colors.primaryBlue,
      },
    },
    articleDetails: {
      background: colors.gray150,
      backgroundExpanded: colors.white,
      border: `2px solid ${colors.primaryBlue}`,
      title: {
        expandedColor: colors.primaryBlue,
        notExpandedColor: colors.primaryBlue,
      },
      body: {
        links: colors.softBlueLinks,
        text: colors.gray550,
      },
    },
    contactForm: {
      placeholder: {
        color: colors.gray550,
      },
      label: {
        color: colors.blue900,
        backgroundColor: colors.white,
      },
      border: {
        color: colors.blue900,
      },
    },
  },
  profile: {
    backgroundColor: colors.white,
    leftMenu: {
      button: {
        color: colors.gray550,
        inactive: {
          backgroundColor: colors.white,
          borderRadius: '0px',
        },
        active: {
          backgroundColor: colors.gray150,
          borderRadius: '12px',
        },
        hover: {
          backgroundColor: colors.gray150,
        },
      },
      profile: {
        activeIcon: '/icons/user_blue.svg',
        inactiveIcon: '/icons/user_gray550.svg',
      },
      messages: {
        activeIcon: '/icons/messages_blue.svg',
        inactiveIcon: '/icons/messages_gray550.svg',
        notificationCount: {
          backgroundColor: colors.primaryBlue,
          border: `2px solid ${colors.gray150}`,
          color: colors.white,
        },
      },
      appearance: {
        activeColor: colors.primaryBlue,
        inactiveColor: colors.gray550,
      },
      privacyPolicy: {
        activeIcon: '/icons/privacy-policy_blue.svg',
        inactiveIcon: '/icons/privacy-policy_gray550.svg',
      },
      termsOfService: {
        activeIcon: '/icons/terms-of-service_blue.svg',
        inactiveIcon: '/icons/terms-of-service_gray550.svg',
      },
      logout: {
        activeIcon: '/icons/logout_blue.svg',
        inactiveIcon: '/icons/logout_gray550.svg',
      },
    },
  },
  components: {
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: colors.blue650,
          '&.Mui-focused': {
            color: colors.blue650,
            backgroundColor: colors.white,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: {
          borderColor: colors.blue650,
          borderWidth: '1px',
        },
        root: {
          '& .MuiInputBase-input': {
            color: `${colors.gray550} !important`,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.blue900,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.blue900,
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          color: colors.gray550,
        },
      },
    },
    MuiTextField: {
      color: colors.blue900,
      label: {
        color: colors.blue900,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          marginBottom: 2,
          '& .MuiInputLabel-asterisk': {
            display: 'none',
          },
          // only show the asterisk if the label is shrinked(focused)
          '& .MuiInputLabel-root.Mui-required[data-shrink="true"]::after': {
            content: '" *"',
            color: colors.blue650,
          },
          '& .MuiOutlinedInput-notchedOutline': {
            '& fieldset': {
              borderColor: colors.blue900,
            },
            '&:hover fieldset': {
              borderColor: colors.blue900,
            },
          },

          '& .MuiInputBase-root': {
            borderColor: colors.blue900,
            color: colors.blue900,
            fontFamily: fonts.regular,
            fontSize: 16,
            fontWeight: 400,
            lineHeight: '24px',
            letterSpacing: 0.5,
            '& fieldset': {
              borderColor: colors.blue900,
            },
          },
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              color: colors.blue900,
              borderColor: colors.blue650,
            },
            '&:hover fieldset': {
              color: colors.blue900,
              borderColor: colors.blue900,
            },
            '&.Mui-focused fieldset': {
              color: colors.blue900,
              borderColor: colors.blue650,
            },
            '& input': {
              color: colors.blue900,
            },
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: colors.blue650,
          },
        }),
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: colors.blue650,
          '&.Mui-checked': {
            color: colors.blue650,
          },
        },
      },
    },
  },
  layout: {
    defaults: {
      backgroundColor: colors.white,
    },
  },
});

export default frostLightTheme;
